/**
 * Export (Excel/PDF) della rendicontazione costi personale — backend
 * GET /costo-personale/export (services/exportCostoPersonale.js +
 * exportRenderers.js). Due ambiti: "vista corrente" (stessi filtri
 * applicati a video, incluso RSA singola se impostata) o "tutte le RSA"
 * nel periodo selezionato (ignora il filtro RSA di proposito); per
 * "tutte le RSA" l'utente sceglie anche la modalità (un file unico o uno
 * zip con un file per RSA) — entrambe richieste esplicitamente da Matteo,
 * sessione 2026-09-16.
 *
 * Il download usa fetch diretto (non l'hook useApi, pensato per JSON) con
 * l'Authorization header preso da getAccessToken() — stesso meccanismo di
 * AuthContext.apiRequest ma con risposta letta come blob invece che JSON,
 * per poi triggerare il download via URL.createObjectURL.
 *
 * Il dettaglio riga-per-riga per singolo nominativo è OFF di default (dato
 * più sensibile del solo aggregato) — checkbox esplicita per includerlo,
 * richiesta da Matteo in un giro successivo al primo export del
 * 2026-09-16.
 */
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiConfig } from "../../config/authConfig";

function nomeFileDaContentDisposition(header, fallback) {
  if (!header) return fallback;
  const match = /filename="?([^"]+)"?/.exec(header);
  return match ? match[1] : fallback;
}

const FORMATI = [
  { value: "excel", label: "Excel" },
  { value: "pdf", label: "PDF" },
];

export default function ExportPanel({ filtriCorrente }) {
  const { getAccessToken } = useAuth();
  const [aperto, setAperto] = useState(false);
  const [formato, setFormato] = useState("excel");
  const [ambito, setAmbito] = useState("corrente");
  const [modalita, setModalita] = useState("unico");
  const [includiDettaglio, setIncludiDettaglio] = useState(false);
  const [scaricando, setScaricando] = useState(false);
  const [errore, setErrore] = useState(null);

  async function scarica() {
    setScaricando(true);
    setErrore(null);
    try {
      const params = new URLSearchParams();
      params.set("format", formato);
      params.set("scope", ambito);
      if (ambito === "massivo") params.set("modalita", modalita);
      if (filtriCorrente.periodoDa)
        params.set("periodoDa", filtriCorrente.periodoDa);
      if (filtriCorrente.periodoA)
        params.set("periodoA", filtriCorrente.periodoA);
      if (filtriCorrente.mansione)
        params.set("mansione", filtriCorrente.mansione);
      if (filtriCorrente.centroCosto)
        params.set("centroCosto", filtriCorrente.centroCosto);
      if (filtriCorrente.armonizzazione)
        params.set("armonizzazione", filtriCorrente.armonizzazione);
      if (filtriCorrente.gruppo) params.set("gruppo", filtriCorrente.gruppo);
      if (filtriCorrente.codiceFiscale) {
        params.set("codiceFiscale", filtriCorrente.codiceFiscale);
      }
      // RSA/raggruppamento hanno senso solo per "vista corrente": lo scope
      // "massivo" ignora sempre il filtro RSA lato backend, e raggruppa
      // sempre per RSA come prima dimensione (vedi buildDatiExportMassivo).
      if (ambito === "corrente") {
        if (filtriCorrente.rsa) params.set("rsa", filtriCorrente.rsa);
        params.set("groupBy", filtriCorrente.groupBy);
      }
      if (includiDettaglio) params.set("includiDettaglio", "1");

      const token = await getAccessToken();
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(
        `${apiConfig.baseUrl}/costo-personale/export?${params.toString()}`,
        { headers },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || data.error || `Errore ${res.status}`);
      }
      const blob = await res.blob();
      const estensione = formato === "excel" ? "xlsx" : "pdf";
      const filename = nomeFileDaContentDisposition(
        res.headers.get("content-disposition"),
        `costo-personale.${estensione}`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setAperto(false);
    } catch (e) {
      setErrore(e.message || "Errore durante l'export");
    } finally {
      setScaricando(false);
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
      >
        ⬇️ Esporta
      </button>
      {aperto && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-10 space-y-3">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Formato</p>
            <div className="flex gap-2">
              {FORMATI.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setFormato(o.value)}
                  className={`flex-1 px-2 py-1 text-sm rounded border ${
                    formato === o.value
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-300 text-gray-600"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Ambito</p>
            <label className="flex items-center gap-2 text-sm py-0.5">
              <input
                type="radio"
                checked={ambito === "corrente"}
                onChange={() => setAmbito("corrente")}
              />
              Vista corrente (filtri applicati)
            </label>
            <label className="flex items-center gap-2 text-sm py-0.5">
              <input
                type="radio"
                checked={ambito === "massivo"}
                onChange={() => setAmbito("massivo")}
              />
              Tutte le RSA (periodo selezionato)
            </label>
          </div>

          {ambito === "massivo" && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Modalità</p>
              <label className="flex items-center gap-2 text-sm py-0.5">
                <input
                  type="radio"
                  checked={modalita === "unico"}
                  onChange={() => setModalita("unico")}
                />
                Un unico file (tutte le RSA)
              </label>
              <label className="flex items-center gap-2 text-sm py-0.5">
                <input
                  type="radio"
                  checked={modalita === "perRsa"}
                  onChange={() => setModalita("perRsa")}
                />
                Un file per RSA (zip)
              </label>
            </div>
          )}

          <div>
            <label className="flex items-center gap-2 text-sm py-0.5">
              <input
                type="checkbox"
                checked={includiDettaglio}
                onChange={(e) => setIncludiDettaglio(e.target.checked)}
              />
              Includi dettaglio nominativi
            </label>
            <p className="text-xs text-gray-400 pl-6">
              Elenco riga per riga per singola persona — escluso di default, il
              file resta solo aggregato.
            </p>
          </div>

          {errore && <p className="text-xs text-red-600">{errore}</p>}

          <button
            type="button"
            onClick={scarica}
            disabled={scaricando}
            className="w-full px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {scaricando ? "Generazione…" : "Scarica"}
          </button>
        </div>
      )}
    </div>
  );
}
